/**
 * Player-absences module (Tier 1 #4a, pre-tournament snapshot).
 *
 * Translates a list of absent key players into a team-level ELO penalty
 * via per-player "criticality" scoring. Captures information the ELO+Poisson
 * model can't see (injuries, suspensions, federation bans, late withdrawals).
 *
 *   criticality = α·(minutes_qual / 720)
 *               + β·(market_value_mil / 150)
 *               + γ·((pos_mult − 1.0) / 0.5)            e [0, 1]
 *
 *   per-player ELO penalty = −5 − 75·criticality
 *   per-team ELO penalty   = S penalty, clamped to [−ABSENCE_CAP, 0]
 */
import absencesData from '../../data/sim/absences.json';

/** Default weights from the calibration sweep run 2026-05-17 on WC 2014/18/22. */
export const DEFAULT_ABSENCE_WEIGHTS = { alpha: 0.0, beta: 0.8, gamma: 0.2 };

const POSITION_MULTIPLIER = {
  GK: 1.5,
  CB: 1.3,
  FB: 1.1,
  DM: 1.1,
  CM: 1.1,
  AM: 1.0,
  WG: 1.0,
  ATT: 1.0,
};

const MINUTES_REF = 720;       // 8 qualifier matches × 90 min
const VALUE_REF_MIL = 150;     // global top market value tier (Mbappé/Haaland-era reference)
const POS_OFFSET_DENOM = 0.5;  // (max_mult − base_mult) so GK → 1.0, ATT → 0.0
const PER_PLAYER_BASE = 5;
const PER_PLAYER_RANGE = 75;
const TEAM_PENALTY_CAP = 150;  // no team can lose more than 150 ELO from absences

let _engineAbsencesEnabled = true;
export function setEngineAbsencesEnabled(v) {
  _engineAbsencesEnabled = v;
}
export function isEngineAbsencesEnabled() {
  return _engineAbsencesEnabled;
}

const STAGE_ORDER = ['group', 'r32', 'r16', 'qf', 'sf', '3rd', 'final'];

function stageIndex(s) {
  const i = STAGE_ORDER.indexOf(s);
  return i === -1 ? 0 : i;
}

/** Returns true if this absence is in effect during the given stage. */
export function appliesAtStage(absence, stage) {
  const from = absence.applies_from_stage ?? 'group';
  return stageIndex(stage) >= stageIndex(from);
}

/** Per-player criticality e [0, 1]. */
export function criticality(a, w) {
  const minutesNorm = Math.min(1, a.minutes_qual / MINUTES_REF);
  const valueNorm = Math.min(1, a.market_value_mil / VALUE_REF_MIL);
  const posMult = POSITION_MULTIPLIER[a.position] ?? 1.0;
  const posOffset = (posMult - 1.0) / POS_OFFSET_DENOM;  // [0, 1] for ATT..GK
  const raw = w.alpha * minutesNorm + w.beta * valueNorm + w.gamma * posOffset;
  return Math.max(0, Math.min(1, raw));
}

/** Per-player ELO penalty (negative). */
export function playerPenalty(a, w) {
  return -(PER_PLAYER_BASE + PER_PLAYER_RANGE * criticality(a, w));
}

/** Sum of per-player penalties for a team, clamped to [-cap, 0]. */
export function teamPenalty(absences, w, stage = 'group') {
  const active = absences.filter((a) => appliesAtStage(a, stage));
  const raw = active.reduce((sum, a) => sum + playerPenalty(a, w), 0);
  return Math.max(-TEAM_PENALTY_CAP, raw);
}

let customAbsencesMap = {};
export function setCustomAbsencesMap(map) {
  customAbsencesMap = map || {};
}

const file = absencesData;

/** Look up historical absences for a (year, team_id) pair. */
export function historicalAbsences(year, teamId) {
  return file.historical?.[String(year)]?.[teamId] ?? [];
}

/** Look up current (live) absences for a team_id (used by match.ts at runtime). */
export function currentAbsences(teamId) {
  const base = file.current?.[teamId] || [];
  const customNames = customAbsencesMap[teamId] || [];
  if (customNames.length === 0) return base;
  
  const customList = customNames.map(name => ({
    player: name,
    position: 'ATT', // default
    minutes_qual: 0,
    market_value_mil: 120, // high impact
    reason: 'Scénario personnalisé'
  }));
  
  return [...base, ...customList];
}
